/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikProps } from "formik";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { ScreenReaderHeading } from "../../components/ScreenReaderHeading";
import { RightSidePortalContext } from "../../components/RightSideWrapper/RightSidePortalContext";
import { SubFormNavigationProps } from "../../components/SubForm";
import { UnsavedChangesModal } from "../../components/UnsavedChangesModal";
import { gameConceptBigIdLength, xboxOnboardSiteLocationPrefix } from "../../constants";
import { areFormValuesEqual } from "../../helpers/areFormValuesEqual";
import {
  isAaaGameInformationAvailabilityFormValid,
  isAaaGameInformationConfigurationsFormValid,
  isAaaGameInformationDetailsFormValid,
  isAaaGameInformationDistributionInformationFormValid,
  isAaaGameInformationGameExpectationsValid,
  isAaaGameInformationSupplementaryDataFormValid,
  isAaaGameInformationTechnicalCapabilitiesFormValid,
  isAaaGameInformationUgcFormValid,
  isAaaGameInformationXboxNetworkFormValid,
  isIdXboxGameInformationConfigurationsFormValid,
  isIdXboxGameInformationDetailsFormValid,
  isIdXboxGameInformationGameExpectationsValid,
  isIdXboxGameInformationPricingAndAvailabilityFormValid,
  isIdXboxGameInformationSupplementaryDataFormValid,
  isIdXboxGameInformationTechnicalCapabilitiesFormValid,
  isIdXboxGameInformationUgcFormValid,
  isIdXboxGameInformationXboxNetworkFormValid,
} from "../../helpers/formValidation";
import {
  isGameConceptHardRejected,
  isGameConceptSoftRejected,
} from "../../helpers/gameConceptHelpers";
import { calculateGameConceptProgress } from "../../helpers/gameConceptProgress";
import { getGameConceptSellerEligibility } from "../../helpers/gameConceptSellerEligibility";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { useUnsavedChangesGuard } from "../../helpers/useUnsavedChangesGuard";
import { useStrings } from "../../localization/localizationHelpers";
import { CrmGameConceptStatus, CurrentGameConcept } from "../../models/crmStatus";
import { Programs } from "../../models/programsConfig";
import { CrmActions } from "../../redux/actions/crmApiActions";
import { gameConceptPageActions } from "../../redux/actions/gameConceptPageActions";
import { programsActions } from "../../redux/actions/programsActions";
import {
  getGameConceptPageData,
  getGameConceptPageError,
  getGameConceptPageIsLoading,
  getGameConceptPageIsSaving,
  getGameConceptPageIsSubmitting,
  getGameConceptPageState,
} from "../../redux/selectors/gameConceptPageSelectors";
import { getIsLoadingGameConcept, getIsSavingForm } from "../../redux/selectors/statusSelectors";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import { AaaGameInformationSubFormsId } from "../../subForms/aaaGameInformation/AaaGameInformationPageConstants";
import { GameInformationSubFormsId } from "../../subForms/idXboxGameInformation/GameInformationPageConstants";
import { DeleteGameConceptModal } from "./components/DeleteGameConceptModal";
import { GameConceptHeader } from "./components/GameConceptHeader";
import { GameConceptSections } from "./components/GameConceptSections";
import { GameConceptSidePanel } from "./components/GameConceptSidePanel";
import { GameConceptStoreIdBanner } from "./components/GameConceptStoreIdBanner";
import { GameConceptTlaBanner } from "./components/GameConceptTlaBanner";
import { SubmitGameConceptModal } from "./components/SubmitGameConceptModal";
import * as styles from "./GameConceptPage.module.css";
import { useGameConceptFormConfig } from "./useGameConceptFormConfig";

interface RouteParams {
  id: string;
}

const GameConceptPage: React.FC = () => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.EnrollmentFinished);
  const { id } = useParams<RouteParams>();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const formRefsMap = useRef<Record<string, React.RefObject<FormikProps<any>>>>({});
  const latestValuesRef = useRef<Record<string, Record<string, unknown>>>({});
  const initialValuesRef = useRef<Record<string, Record<string, unknown>>>({});

  const searchParams = new URLSearchParams(location.search);
  const [isNewConcept, _setIsNewConcept] = useState(searchParams.get("isNew") === "true");

  const getFormRef = useCallback((subFormId: string): React.RefObject<FormikProps<any>> => {
    if (!formRefsMap.current[subFormId]) {
      formRefsMap.current[subFormId] = React.createRef<FormikProps<any>>();
    }
    return formRefsMap.current[subFormId];
  }, []);

  const data = useSelector(getGameConceptPageData);
  const accountType = data?.accountType ?? "id";
  const canSubmitIdApplication =
    useSelector(getXboxOnboardData)?.hubPermissions.canSubmitIdApplication === true;
  const isLoading = useSelector(getGameConceptPageIsLoading);
  const isSaving = useSelector(getIsSavingForm);
  const isSavingGameConcept = useSelector(getGameConceptPageIsSaving);
  const isSubmitting = useSelector(getGameConceptPageIsSubmitting);
  const isDeletingGameConcept = useSelector(getIsLoadingGameConcept);
  const error = useSelector(getGameConceptPageError);
  const {
    sellerDetails,
    sellerDetailsGameConceptId,
    isSellerDetailsLoading,
    sellerDetailsError,
    isSavingStoreId,
    storeIdError,
  } = useSelector(getGameConceptPageState);
  const { isActiveSeller, hasPassedVetting } = getGameConceptSellerEligibility(
    sellerDetailsGameConceptId === id ? sellerDetails : null
  );
  const isSellerEligible =
    !isSellerDetailsLoading && !sellerDetailsError && isActiveSeller && hasPassedVetting;
  const loadedGameConceptId = data?.gameConceptId;

  const [
    gameConceptBreadcrumb,
    gameConceptInvalidTitle,
    gameConceptInvalidDescription,
    gameConceptLoadErrorTitle,
    gameConceptNewSubmissionTitle,
    gameConceptSaveError,
    missingReleaseNotes,
    sellerRequirementsError,
    bigIdRequired,
    bigIdInvalidLength,
  ] = useStrings(
    "gameConceptBreadcrumb",
    "gameConceptInvalidTitle",
    "gameConceptInvalidDescription",
    "gameConceptLoadErrorTitle",
    "gameConceptNewSubmissionTitle",
    "gameConceptSaveError",
    "missingReleaseNotes",
    "gameConceptSellerRequirementsError",
    "gameConceptBigIdRequired",
    "gameConceptBigIdInvalidLength"
  );
  const gameConceptErrorMessage =
    error === "missingReleaseNotes"
      ? missingReleaseNotes
      : error === "gameConceptSellerRequirementsError"
      ? sellerRequirementsError
      : error === "gameConceptBigIdRequired"
      ? bigIdRequired
      : error === "gameConceptBigIdInvalidLength"
      ? bigIdInvalidLength
      : gameConceptSaveError;

  const [openSections, setOpenSections] = useState<Set<number>>(new Set());
  const [startedSections, setStartedSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isPageReady || !id) {
      return;
    }
    dispatch(gameConceptPageActions.fetchGameConcept.start(id));
  }, [dispatch, id, isPageReady]);

  useEffect(() => {
    if (isPageReady && id && loadedGameConceptId === id) {
      dispatch(gameConceptPageActions.fetchSellerDetails.start(id));
    }
  }, [dispatch, id, isPageReady, loadedGameConceptId]);

  const isDraft = data?.isDraft ?? true;
  const conceptState = data?.state ?? null;
  const isOnboardingApproved = data?.isOnboardingApproved ?? false;
  const showTlaPendingBanner =
    conceptState?.status === CrmGameConceptStatus.AdditionalContractsSent;
  const formData = data?.formData as Record<string, Record<string, unknown>> | null | undefined;
  const gameTitle =
    (formData?.details?.gameTitle as string | undefined) || gameConceptNewSubmissionTitle;
  const formattedTlaSentDate = data?.tlaSentDate
    ? new Date(data.tlaSentDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : undefined;

  const isHardRejected = isGameConceptHardRejected(conceptState?.status, conceptState?.subStatus);
  const showSubmissionProgress =
    isDraft ||
    !conceptState ||
    conceptState.status === CrmGameConceptStatus.Unset ||
    conceptState.status === CrmGameConceptStatus.MoreInfoNeeded ||
    isGameConceptSoftRejected(conceptState.status, conceptState.subStatus);

  // Adapter from the purpose-built endpoint's concept state to the shape the
  // status banner expects. Soft-rejected / MoreInfoNeeded stay editable, so the
  // partner can act on the reviewer's guidance shown in the banner.
  const currentGameConcept: CurrentGameConcept | undefined = conceptState
    ? {
        id: conceptState.id,
        crmGameConceptStatus: conceptState.status,
        crmGameConceptSubStatus: conceptState.subStatus,
        crmGameConceptApprovalComments: conceptState.approvalStatusComments,
        submittedDate: conceptState.submittedDate,
        acceptedDate: conceptState.acceptedDate,
      }
    : undefined;

  const isReadOnly =
    data?.canEdit === false ||
    conceptState?.status === CrmGameConceptStatus.Accepted ||
    conceptState?.status === CrmGameConceptStatus.UnderReview ||
    conceptState?.status === CrmGameConceptStatus.OnHold ||
    conceptState?.status === CrmGameConceptStatus.AdditionalContractsSent ||
    isHardRejected;

  const captureInitialValues = useCallback((subFormId: string, formik: FormikProps<any>) => {
    if (!initialValuesRef.current[subFormId]) {
      initialValuesRef.current[subFormId] = formik.initialValues;
    }
  }, []);

  const hasUnsavedChanges = useCallback(() => {
    if (isReadOnly) {
      return false;
    }

    return Object.entries(formRefsMap.current).some(([subFormId, ref]) => {
      const formik = ref.current;
      if (formik) {
        captureInitialValues(subFormId, formik);
        return !areFormValuesEqual(formik.values, initialValuesRef.current[subFormId]);
      }

      const latestValues = latestValuesRef.current[subFormId];
      const initialValues = initialValuesRef.current[subFormId];
      return !!latestValues && !!initialValues && !areFormValuesEqual(latestValues, initialValues);
    });
  }, [captureInitialValues, isReadOnly]);

  const { isNavigationBlocked, allowNextNavigation, leavePage, stayOnPage } =
    useUnsavedChangesGuard(hasUnsavedChanges, !isReadOnly);

  const { subFormsOrder, formId, program, subFormsList } = useGameConceptFormConfig(
    accountType,
    currentGameConcept
  );

  // Reset per-section UI state when switching to a different concept so a
  // previously-opened concept's started/open sections never leak into another
  // one (e.g. navigating from a started concept straight into a brand-new one).
  useEffect(() => {
    setStartedSections(new Set());
    setOpenSections(new Set());
    formRefsMap.current = {};
    latestValuesRef.current = {};
    initialValuesRef.current = {};
  }, [id]);

  // Initialize startedSections from CRM data (sections previously visited).
  // Gate on the loaded data belonging to the current route id: on the first
  // render after navigation, redux may still hold the previously-opened
  // concept's formData (with isStarted set), which must not seed this concept.
  useEffect(() => {
    if (!formData || !subFormsOrder || data?.gameConceptId !== id) {
      return;
    }
    const alreadyStarted = new Set<string>();
    for (const sfId of subFormsOrder) {
      const sectionData = formData[sfId];
      if (sectionData?.isStarted) {
        alreadyStarted.add(sfId);
      }
    }
    if (alreadyStarted.size > 0) {
      setStartedSections(prev => {
        const merged = new Set(prev);
        for (const sfId of alreadyStarted) {
          merged.add(sfId);
        }
        return merged;
      });
    }
  }, [formData, subFormsOrder, data?.gameConceptId, id]);

  const allSectionsStarted = (subFormsOrder as string[]).every(sfId => startedSections.has(sfId));

  const onSaveFormToCrm = useCallback(
    (subFormId: string) => {
      dispatch(gameConceptPageActions.saveGameConcept.start(id, formId, subFormId, isDraft));
    },
    [dispatch, id, formId, isDraft]
  );

  const onForwardNavigation = useCallback(() => {
    // No-op in accordion mode — all sections accessible independently
  }, []);

  const _onBackNavigation = useCallback(() => {
    // No-op in accordion mode
  }, []);

  const onCancel = useCallback(() => {
    history.push(`${xboxOnboardSiteLocationPrefix}/dashboard`);
  }, [history]);

  const getSubFormProps = useCallback(
    (subFormId: string): SubFormNavigationProps => ({
      formRef: getFormRef(subFormId),
      onCancel,
      onBackNavigation: undefined,
      onForwardNavigation,
      onSaveFormToCrm,
      skipScrollToTop: true,
      hideNavigation: true,
    }),
    [onCancel, onForwardNavigation, onSaveFormToCrm, getFormRef]
  );

  // Section validity tracking
  const validators = useMemo(() => {
    const idV: Record<string, (data: any) => Promise<boolean>> = {
      [GameInformationSubFormsId.ConceptDetails]: isIdXboxGameInformationDetailsFormValid,
      [GameInformationSubFormsId.GameExpectations]: isIdXboxGameInformationGameExpectationsValid,
      [GameInformationSubFormsId.PricingAndAvailability]:
        isIdXboxGameInformationPricingAndAvailabilityFormValid,
      [GameInformationSubFormsId.TechnicalCapabilities]:
        isIdXboxGameInformationTechnicalCapabilitiesFormValid,
      [GameInformationSubFormsId.Configurations]: isIdXboxGameInformationConfigurationsFormValid,
      [GameInformationSubFormsId.XboxNetwork]: isIdXboxGameInformationXboxNetworkFormValid,
      [GameInformationSubFormsId.Ugc]: isIdXboxGameInformationUgcFormValid,
      [GameInformationSubFormsId.SupplementaryData]:
        isIdXboxGameInformationSupplementaryDataFormValid,
    };

    const aaaV: Record<string, (data: any) => Promise<boolean>> = {
      [AaaGameInformationSubFormsId.ConceptDetails]: isAaaGameInformationDetailsFormValid,
      [AaaGameInformationSubFormsId.DistributionInformation]:
        isAaaGameInformationDistributionInformationFormValid,
      [AaaGameInformationSubFormsId.GameExpectations]: isAaaGameInformationGameExpectationsValid,
      [AaaGameInformationSubFormsId.Availability]: isAaaGameInformationAvailabilityFormValid,
      [AaaGameInformationSubFormsId.TechnicalCapabilities]:
        isAaaGameInformationTechnicalCapabilitiesFormValid,
      [AaaGameInformationSubFormsId.Configurations]: isAaaGameInformationConfigurationsFormValid,
      [AaaGameInformationSubFormsId.XboxNetwork]: isAaaGameInformationXboxNetworkFormValid,
      [AaaGameInformationSubFormsId.Ugc]: isAaaGameInformationUgcFormValid,
      [AaaGameInformationSubFormsId.SupplementaryData]:
        isAaaGameInformationSupplementaryDataFormValid,
    };

    return accountType === Programs.Aaa ? aaaV : idV;
  }, [accountType]);

  const [sectionValidity, setSectionValidity] = useState<Record<string, boolean>>({});
  const allSectionsValid =
    allSectionsStarted && (subFormsOrder as string[]).every(sfId => sectionValidity[sfId] === true);
  const conceptProgress = calculateGameConceptProgress(
    subFormsOrder,
    startedSections,
    sectionValidity
  );

  // Helper to get the best available data for a subform section
  const getSectionData = useCallback(
    (subFormId: string) => {
      const ref = formRefsMap.current[subFormId];
      return ref?.current?.values ?? latestValuesRef.current[subFormId] ?? formData?.[subFormId];
    },
    [formData]
  );

  // Validate all sections when data loads or changes
  useEffect(() => {
    if (!formData && startedSections.size === 0) {
      return;
    }

    const runValidation = async () => {
      const results: Record<string, boolean> = {};
      for (const subFormId of subFormsOrder) {
        const sectionData = getSectionData(subFormId);
        const validator = validators[subFormId];
        if (validator && sectionData) {
          try {
            results[subFormId] = await validator(sectionData);
          } catch {
            results[subFormId] = false;
          }
        } else {
          results[subFormId] = false;
        }
      }
      setSectionValidity(results);
    };

    runValidation();
  }, [formData, subFormsOrder, getSectionData, startedSections, validators]);

  // Re-validate on an interval to pick up live Formik edits
  useEffect(() => {
    const interval = setInterval(async () => {
      const results: Record<string, boolean> = {};
      for (const subFormId of subFormsOrder) {
        // Capture latest Formik values into ref so they survive accordion close
        const ref = formRefsMap.current[subFormId];
        if (ref?.current?.values) {
          captureInitialValues(subFormId, ref.current);
          latestValuesRef.current[subFormId] = ref.current.values;
        }
        const sectionData = getSectionData(subFormId);
        const validator = validators[subFormId];
        if (validator && sectionData) {
          try {
            results[subFormId] = await validator(sectionData);
          } catch {
            results[subFormId] = false;
          }
        } else {
          results[subFormId] = false;
        }
      }
      setSectionValidity(results);
    }, 1000);
    return () => clearInterval(interval);
  }, [captureInitialValues, subFormsOrder, getSectionData, validators]);

  const [isSaveAndExitPending, setIsSaveAndExitPending] = useState(false);

  const buildSavePayload = () => {
    const payload: Record<string, unknown> = {};

    for (let i = 0; i < subFormsOrder.length; i++) {
      const subFormId = subFormsOrder[i] as string;
      // Only sections the user actually opened count as started. Sections that
      // were never opened are intentionally omitted from the payload so the CRM
      // mappers (which mark a section "started" whenever its data is present)
      // never flag them as started.
      const wasStarted = startedSections.has(subFormId) || openSections.has(i);
      if (!wasStarted) {
        continue;
      }

      const ref = formRefsMap.current[subFormId];
      let data: Record<string, unknown> | undefined;
      if (openSections.has(i) && ref?.current?.values) {
        data = ref.current.values;
      } else if (latestValuesRef.current[subFormId]) {
        data = latestValuesRef.current[subFormId];
      } else if (formData?.[subFormId]) {
        data = formData[subFormId] as Record<string, unknown>;
      }

      if (data) {
        payload[subFormId] = { ...data, isStarted: true };
      }
    }

    // Also update Redux for consistency
    for (const [subFormId, data] of Object.entries(payload)) {
      dispatch(
        programsActions.updateSubFormData(program, {
          formId,
          subFormId,
          subForm: data as any,
        })
      );
    }

    return payload;
  };

  const handleSaveAndClose = () => {
    const payload = buildSavePayload();
    dispatch(gameConceptPageActions.saveGameConcept.start(id, formId, "", isDraft, payload));
    setIsSaveAndExitPending(true);
  };

  // Persist the concept before asking for its Big ID, so canceling the dialog
  // still leaves the form saved and the concept available in CRM.
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [pendingSubmitGameConceptId, setPendingSubmitGameConceptId] = useState<string | null>(null);
  const isSubmitSavePending = pendingSubmitGameConceptId !== null;
  const handleSubmit = () => {
    if (isBusy || !isSellerEligible || data?.canSubmit !== true) {
      return;
    }
    const payload = buildSavePayload();
    setPendingSubmitGameConceptId(id);
    dispatch(gameConceptPageActions.saveGameConcept.start(id, formId, "", isDraft, payload));
  };
  const handleConfirmSubmit = (bigId: string) => {
    if (
      isBusy ||
      !isSellerEligible ||
      data?.canSubmit !== true ||
      (!!bigId.trim() && bigId.trim().length !== gameConceptBigIdLength)
    ) {
      return;
    }
    setShowSubmitConfirm(false);
    dispatch(gameConceptPageActions.submitGameConcept.start(id, bigId.trim()));
  };

  const submitSaveStartedRef = useRef(false);
  useEffect(() => {
    setShowSubmitConfirm(false);
    setPendingSubmitGameConceptId(null);
    submitSaveStartedRef.current = false;
  }, [id]);
  useEffect(() => {
    if (isSubmitSavePending && isSavingGameConcept) {
      submitSaveStartedRef.current = true;
    }
    if (isSubmitSavePending && submitSaveStartedRef.current && !isSavingGameConcept) {
      setPendingSubmitGameConceptId(null);
      submitSaveStartedRef.current = false;
      if (error || pendingSubmitGameConceptId !== id) {
        // Save failed — keep the dialog closed and surface the error for retry.
        return;
      }
      setShowSubmitConfirm(true);
    }
  }, [isSubmitSavePending, isSavingGameConcept, error, id, pendingSubmitGameConceptId]);

  // Navigate to the dashboard once the submit completes SUCCESSFULLY. We track the
  // submit lifecycle here (separate from the save-before-confirmation above) and
  // never navigate on failure so the error stays visible for the user to retry.
  const submitStartedRef = useRef(false);
  useEffect(() => {
    if (isSubmitting) {
      submitStartedRef.current = true;
    }
    if (submitStartedRef.current && !isSubmitting) {
      submitStartedRef.current = false;
      if (error) {
        return;
      }
      allowNextNavigation();
      history.push(`${xboxOnboardSiteLocationPrefix}/dashboard`);
    }
  }, [isSubmitting, error, allowNextNavigation, history]);

  const handleCancel = () => {
    history.push(`${xboxOnboardSiteLocationPrefix}/dashboard`);
  };

  // Navigate to dashboard only after the game-concept save completes SUCCESSFULLY.
  // We track this page's own save lifecycle (not the shared global saving flag) so
  // an unrelated/overlapping save can't trip the transition, and we never navigate
  // away on failure — that would silently discard the user's edits.
  const saveStartedRef = useRef(false);
  useEffect(() => {
    if (isSaveAndExitPending && isSavingGameConcept) {
      saveStartedRef.current = true;
    }
    if (isSaveAndExitPending && saveStartedRef.current && !isSavingGameConcept) {
      setIsSaveAndExitPending(false);
      saveStartedRef.current = false;
      if (error) {
        // Save failed — stay on the page and surface the error so the user can retry.
        return;
      }
      allowNextNavigation();
      history.push(`${xboxOnboardSiteLocationPrefix}/dashboard`);
    }
  }, [isSaveAndExitPending, isSavingGameConcept, error, allowNextNavigation, history]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletePending, setIsDeletePending] = useState(false);

  const handleDelete = () => {
    setIsDeletePending(true);
    setShowDeleteConfirm(false);
    dispatch({
      type: CrmActions.DELETE_GAME_CONCEPT_START,
      program,
      gameConceptId: id,
      redirectToDashboard: false,
    });
  };

  // Navigate to dashboard after delete API call completes
  useEffect(() => {
    if (isDeletePending && !isDeletingGameConcept) {
      allowNextNavigation();
      history.replace(`${xboxOnboardSiteLocationPrefix}/dashboard`);
    }
  }, [isDeletePending, isDeletingGameConcept, allowNextNavigation, history]);

  const handleSectionClick = (index: number) => {
    const subFormId = subFormsOrder[index];
    const isCurrentlyOpen = openSections.has(index);

    if (isCurrentlyOpen) {
      // Persist unsaved values to Redux before closing
      const ref = formRefsMap.current[subFormId];
      if (ref?.current?.values) {
        captureInitialValues(subFormId, ref.current);
        latestValuesRef.current[subFormId] = ref.current.values;
        dispatch(
          programsActions.updateSubFormData(program, {
            formId,
            subFormId,
            subForm: { ...ref.current.values, isStarted: true },
          })
        );
      }
      setOpenSections(prev => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    } else {
      // Mark section as started when opened for the first time
      if (!startedSections.has(subFormId)) {
        setStartedSections(prev => new Set(prev).add(subFormId));
        const existingData = formData?.[subFormId] ?? {};
        dispatch(
          programsActions.updateSubFormData(program, {
            formId,
            subFormId,
            subForm: { ...existingData, isStarted: true } as any,
          })
        );
      }
      setOpenSections(prev => {
        const next = new Set(prev);
        next.add(index);
        return next;
      });
    }
  };

  if (!id) {
    return (
      <PartnerPageLayout breadcrumbLabel={gameConceptBreadcrumb}>
        <div className={styles.errorState}>
          <h2>{gameConceptInvalidTitle}</h2>
          <p>{gameConceptInvalidDescription}</p>
        </div>
      </PartnerPageLayout>
    );
  }

  if (!isPageReady || isLoading) {
    return (
      <PartnerPageLayout breadcrumbLabel={gameConceptBreadcrumb}>
        <PageLoadingSpinner />
      </PartnerPageLayout>
    );
  }

  if (error && !data) {
    return (
      <PartnerPageLayout breadcrumbLabel={gameConceptBreadcrumb}>
        <div className={styles.errorState}>
          <h2>{gameConceptLoadErrorTitle}</h2>
          <p>{error}</p>
        </div>
      </PartnerPageLayout>
    );
  }

  const isSaveInProgress = isSaveAndExitPending;
  const isSubmitInProgress = isSubmitting || isSubmitSavePending;
  const isBusy =
    isSaving || isSavingGameConcept || isSaveInProgress || isSubmitInProgress || isDeletePending;

  return (
    <RightSidePortalContext.Provider value={undefined}>
      <PartnerPageLayout breadcrumbLabel={gameConceptBreadcrumb}>
        <ScreenReaderHeading>{gameTitle}</ScreenReaderHeading>
        <GameConceptHeader
          conceptState={conceptState}
          isDraft={isDraft}
          allSectionsValid={allSectionsValid}
          isReadOnly={isReadOnly}
          isBusy={isBusy}
          isSaveInProgress={isSaveInProgress}
          isSubmitInProgress={isSubmitInProgress}
          isSubmitSavePending={isSubmitSavePending}
          isOnboardingApproved={isOnboardingApproved}
          isSellerEligible={isSellerEligible}
          canSubmit={data?.canSubmit === true}
          onCancel={handleCancel}
          onSave={handleSaveAndClose}
          onSubmit={handleSubmit}
        />

        {error && data && (
          <div className={styles.saveErrorBanner} role="alert">
            {gameConceptErrorMessage}
          </div>
        )}

        {showTlaPendingBanner && data && <GameConceptTlaBanner sentDate={formattedTlaSentDate} />}

        <div className={styles.body}>
          <GameConceptSections
            accountType={accountType}
            currentGameConcept={currentGameConcept}
            isReadOnly={isReadOnly}
            subFormsOrder={subFormsOrder}
            subFormsList={subFormsList}
            openSections={openSections}
            startedSections={startedSections}
            sectionValidity={sectionValidity}
            formData={formData}
            getSubFormProps={getSubFormProps}
            onSectionClick={handleSectionClick}
          >
            {data?.canAddStoreId &&
              !data.bigId?.trim() &&
              conceptState?.status === CrmGameConceptStatus.Accepted && (
                <GameConceptStoreIdBanner
                  key={id}
                  isSaving={isSavingStoreId}
                  hasError={!!storeIdError}
                  onSave={bigId => dispatch(gameConceptPageActions.saveStoreId.start(id, bigId))}
                />
              )}
          </GameConceptSections>
          <GameConceptSidePanel
            accountType={accountType}
            conceptState={conceptState}
            bigId={data?.bigId}
            formData={formData}
            gameTitle={gameTitle}
            isReadOnly={isReadOnly}
            isNewConcept={isNewConcept}
            isOnboardingApproved={isOnboardingApproved}
            canSubmitIdApplication={canSubmitIdApplication}
            showSubmissionProgress={showSubmissionProgress}
            allSectionsValid={allSectionsValid}
            isActiveSeller={isActiveSeller}
            hasPassedVetting={hasPassedVetting}
            isSellerDetailsLoading={isSellerDetailsLoading}
            sellerDetailsError={sellerDetailsError !== null}
            onRetrySellerDetails={() =>
              dispatch(gameConceptPageActions.fetchSellerDetails.start(id))
            }
            conceptProgress={conceptProgress}
            tlaReceivedDate={data?.tlaReceivedDate}
            onDeleteRequest={() => setShowDeleteConfirm(true)}
          />
        </div>
      </PartnerPageLayout>
      <SubmitGameConceptModal
        isOpen={showSubmitConfirm}
        initialBigId={data?.bigId}
        canConfirm={isSellerEligible && data?.canSubmit === true && !isBusy}
        onClose={() => setShowSubmitConfirm(false)}
        onConfirm={handleConfirmSubmit}
      />
      <DeleteGameConceptModal
        gameTitle={gameTitle}
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
      />
      <UnsavedChangesModal isOpen={isNavigationBlocked} onLeave={leavePage} onStay={stayOnPage} />
    </RightSidePortalContext.Provider>
  );
};

export default GameConceptPage;
